// core
import { Injectable } from '@angular/core';
import { FormGroup, AbstractControl } from '@angular/forms';
import { Observable, Subject } from 'rxjs';

// services
import { RaptorActionService } from '../resources/raptor-action.service';
import { RaptorInterstitialService } from './raptor-interstitial.service';
import { RaptorPredicateService } from './raptor-predicate.service';

@Injectable()
export class RaptorStepService {
  action = 'page-loading-forward';
  submitted = false;
  stepReady = false;
  updating = false;
  updating$: Observable<boolean>;
  openRepeaters: any = {};
  editingRepeater = false;
  loading = true;
  loading$: Observable<boolean>;
  editQuestion$: Observable<string>;
  jump$: Observable<string>;

  private loadingSubject: Subject<boolean>;
  private editQuestionSubject: Subject<string>;
  private jumpSubject: Subject<string>;
  private updatingSubject: Subject<boolean>;

  constructor(
    private raptorInterstitialService: RaptorInterstitialService,
    private raptorActionService: RaptorActionService,
    private raptorPredicateService: RaptorPredicateService
  ) {
    this.loadingSubject = new Subject<boolean>();
    this.loading$ = this.loadingSubject.asObservable();

    this.editQuestionSubject = new Subject<string>();
    this.editQuestion$ = this.editQuestionSubject.asObservable();

    this.jumpSubject = new Subject<string>();
    this.jump$ = this.jumpSubject.asObservable();

    this.updatingSubject = new Subject<boolean>();
    this.updating$ = this.updatingSubject.asObservable();

    // initial page load is always forward
    this.loadingState(false);
    this.stepReady = true;
  }

  public next(formData: any, form: FormGroup): Promise<any> {
    let promise;
    let showInterstitial = true;
    this.submitted = true;

    if (form.status === 'VALID') {
      this.setFormHeight();
      this.loadingState(true);

      if (formData.interstitial) {
        if (formData.interstitial.show_if && typeof formData.interstitial.show_if === 'object') {
          showInterstitial = this.raptorPredicateService.validator(formData.interstitial.show_if, form.value);
        }

        if (showInterstitial) {
          window.scrollTo(0, 0);
          this.raptorInterstitialService.open(formData.interstitial);
        }
      }

      promise = this.raptorActionService.next(formData, form)
        .then((response) => {
          if (response.actions && response.actions.redirect) {
            this.executeRedirectAction(formData, showInterstitial, response.actions);
          } else if (formData.interstitial && showInterstitial) {
            this.raptorInterstitialService.close();
          }
          this.resetForm('forward');
          return response;
        })
        .catch((errorResponse) => {
          // This catch block closes interstitial, if present
          // throws error which is handled by raptor-form to display error messages
          if (formData.interstitial && showInterstitial) {
            this.raptorInterstitialService.close();
          }
          throw errorResponse;
        });
    } else {
      promise = new Promise((resolve) => {
        resolve(false);
      });
    }

    return promise;
  }

  public back(formData: any): Promise<any> {
    this.setFormHeight();
    this.loadingState(true);

    return this.raptorActionService.back(formData)
      .then((response) => {
        this.resetForm('reverse');
        return response;
      });
  }

  public update(formData: any, form: FormGroup, question: any): Promise<any> {
    let promise;
    const questionControl: AbstractControl | null = form.get(question.key);

    this.updating = true;
    this.updatingSubject.next(true);

    if (questionControl && questionControl.status === 'VALID') {
      promise = this.raptorActionService.live(formData, form)
        .then((response) => {
          this.updating = false;
          this.updatingSubject.next(false);
          return response;
        })
        .catch((errorResponse) => {
          this.updating = false;
          this.updatingSubject.next(false);
          throw errorResponse;
        });
    } else {
      promise = new Promise((resolve) => {
        this.updating = false;
        this.updatingSubject.next(false);
        resolve(true);
      });
    }

    return promise;
  }

  public goToEdit(formData: any, questionKey: string): Promise<any> {
    this.setFormHeight();
    this.loadingState(true);

    return this.raptorActionService.goToEdit(formData, questionKey)
      .then((response) => {
        this.resetForm('reverse');
        return response;
      });
  }

  public jump(url: string): Promise<any> {
    this.setFormHeight();
    this.loadingState(true);

    return this.raptorActionService.jump(url)
      .then((response) => {
        this.resetForm('reverse');
        this.jumpSubject.next(response);
      });
  }

  public isEditingRepeater() {
    let repeaterOpen = false;

    for (const value in this.openRepeaters) {
      if (this.openRepeaters[value]) {
        repeaterOpen = true;
      }
    }
    this.editingRepeater = repeaterOpen;
  }

  public editRepeater(slug: string, editing: boolean): void {
    this.openRepeaters[slug] = editing;
    this.isEditingRepeater();
  }

  public clearRepeaters(): void {
    this.openRepeaters = {};
    this.editingRepeater = false;
  }

  private setFormHeight() {
    const raptorForm: any = document.querySelector('open-raptor-form form');
    if (raptorForm) {
      const raptorHeight = raptorForm.offsetHeight;
      raptorForm.style.minHeight = `${raptorHeight}px`;
    }
  }

  public resetForm(direction: string): void {
    const raptorForm: any = document.querySelector('open-raptor-form form');

    this.clearRepeaters();
    window.scrollTo(0, 0);
    this.stepReady = false;
    this.action = '';

    // short timeout to clear the page mark and then re-render it
    setTimeout(() => {
      this.loadingState(false);
      this.loading = false;
      this.submitted = false;
      this.action = `page-loading-${direction}`;
      this.stepReady = true;
      raptorForm.style.minHeight = 'auto';
    }, 20);
  }

  public loadingState(value: boolean) {
    this.loading = value;
    this.loadingSubject.next(value);
  }

  public setQuestionToEdit(value: string) {
    this.editQuestionSubject.next(value);
  }

  public clearQuestionToEdit() {
    this.editQuestionSubject.next('');
  }

  private executeRedirectAction(formData: any, showInterstitial: boolean, actions: any) {
    // If interstitial precedes redirection
    if (formData.interstitial && showInterstitial) {
      // raptorInterstitialService.close will ensure redirection happens after
      //  time configured on interstitial has elapsed
      this.raptorInterstitialService.close(() => {
        window.open(actions.redirect, '_self');
      });
    } else {
      // Redirect immediately if no interstital
      window.open(actions.redirect, '_self');
    }
  }
}
